import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import { isAdsEditor } from "../../../../../../../utils/index.ts";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import TextBoxType from "../../../../../../components/common/TextBoxType";
import { TextTypeClass, textTypesStyles } from "../../../../../../constants/TextTypes";
import { defaultFont } from "../../../../../../constants/mock-data";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor.tsx";
import { useActiveObject } from "../../../../../../hooks/useActiveObject.tsx";
import UseAdsEditorUtils from "../../../../../../hooks/useAdsEditorUtils.tsx";
import { useEditor } from "../../../../../../hooks/useEditor";
import { FontItem, TextStyleOptions } from "../../../../../../interfaces/common";
import { IStaticText, LayerType } from "../../../../../../types/layers.ts";
import { loadFonts } from "../../../../../../utils/fonts.ts";
import styles from "./Text.module.scss";

const Text = () => {
	const editor = useEditor();
	const { isPopupView } = useContext(DesignEditorContext);
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.text" });
	const { getFabricLayerTakenConfigs, getFirstAvailableConfig } = UseAdsEditorUtils();
	const [takenValues, setTakenValues] = useState<Set<string>>(new Set());
	const activeObject = useActiveObject() as Required<IStaticText>;
	const { campaignStore } = useAppStore();

	useEffect(() => {
		let watcher = async () => {
			const currentScene = editor?.scene.exportToJSON();
			if (currentScene) {
				const usedConfigs = getFabricLayerTakenConfigs(currentScene?.layers, LayerType.STATIC_TEXT);
				setTakenValues(usedConfigs);
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	const applyAdjustedFontSize = (desiredFontSize: number, scaleFactor: number) => {
		return desiredFontSize / scaleFactor;
	};

	const getScaleFactorFromViewport = () => {
		const transform = editor?.canvas.canvas.viewportTransform;
		return transform ? transform[0] : 1;
	};

	const getTextOptions = async (id: string): Promise<TextStyleOptions | undefined> => {
		const font: FontItem = {
			name: defaultFont.name,
			url: defaultFont.url,
		};
		await loadFonts([font]);
		const style = textTypesStyles.find((style) => style.id === id);
		if (!style) return;
		const scaleFactor = getScaleFactorFromViewport();
		const adjustedFontSize = applyAdjustedFontSize(parseInt(style.style.fontSize), scaleFactor).toFixed(0);

		return {
			id: nanoid(),
			type: "StaticText",
			width: 420,
			text: style.text,
			fontSize: Number(adjustedFontSize),
			fontFamily: font.name,
			textAlign: "center",
			fontStyle: "normal",
			fontURL: font.url,
			fill: style.style.color,
			metadata: {},
			fontWeight: style.style.fontWeight,
			textType: style.type,
			customFontName: "",
		};
	};

	const addObject = async (id: string) => {
		if (!editor) return;

		const isAds = isAdsEditor();
		const currentScene = editor.scene.exportToJSON();

		// Determine the first available config if not in Ads Editor
		let firstAvailableConfig = undefined;
		if (currentScene && !isAds) {
			const usedConfigs = getFabricLayerTakenConfigs(currentScene.layers, LayerType.STATIC_TEXT);
			firstAvailableConfig = getFirstAvailableConfig(usedConfigs, LayerType.STATIC_TEXT);

			if (!firstAvailableConfig) {
				campaignStore.setProperty("nonAvailableConfigSnackbarOpen", true);
				return;
			}
		}

		const options = await getTextOptions(id);
		if (options) {
			if (!isAds && firstAvailableConfig) {
				options.textType = firstAvailableConfig;
			}
			editor.objects.add(options);
		}
	};

	return (
		<Box className={styles.textTapContainer}>
			<MainTabHeader title={t("title")} />
			<Box className={styles.tabs}>
				<Box className={styles.textTapContent}>
					{!isPopupView && (
						<>
							{textTypesStyles.map(({ id, text, type }) => (
								<TextBoxType
									key={id}
									textTypeClass={id as TextTypeClass}
									label={text}
									onClick={() => addObject(id)}
									disabled={!isAdsEditor() && !!type && takenValues.has(type)}
								/>
							))}
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};

const ObservedComponent = observer(Text);
export default ObservedComponent;
